// @tennis-matches/services/persistence/localState
// Migrated from src/services/persistence/localState.js
// Local persistence (localStorage) + schema migrations.

const KEY = "tennis_toolbox_state";
const SCHEMA_VERSION = 1;

// legacy keys from earlier iterations (cleanup + one-time migration)
const LEGACY_KEYS = [
  "tennis_app_state_v2",
  "tennis_app_state",
  "tennis-matches-state",
];

/**
 * Clears persisted local state (used for "Turnier neu starten").
 * Keeps tenant id by default (Phase 1), but you can also wipe it if needed later.
 */
export function clearLocalState({ keepTenant = true } = {}) {
  const TENANT_LS_KEY = "ttb_active_tenant_id";
  try { localStorage.removeItem(KEY); } catch {}
  try {
    for (const k of LEGACY_KEYS) {
      try { localStorage.removeItem(k); } catch {}
    }
  } catch {}
  if (!keepTenant) {
    try { localStorage.removeItem(TENANT_LS_KEY); } catch {}
  }
}

/**
 * Loads application state from localStorage.
 * Handles legacy state migration automatically.
 * @returns {Object|null} The loaded state object, or null if not found/invalid
 */
export function loadState() {
  // One-time: migrate legacy state (if new key is missing) and always remove legacy keys.
  const raw = localStorage.getItem(KEY);

  if (!raw) {
    const legacy = readLegacyState();
    if (!legacy) return null;

    const migrated = migrate(legacy);
    try {
      localStorage.setItem(KEY, JSON.stringify(sanitizeForSave(migrated)));
    } catch {
      // ignore write errors (quota/private mode)
    }
    removeLegacyKeys();
    return migrated;
  }
  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  return migrate(parsed);
}

/**
 * Saves application state to localStorage and remote storage (best-effort).
 * Remote save is handled by the traffic-controller (persistenceservice).
 * @param {Object} state - The application state to save
 * @returns {Object} cleaned state
 */
export function saveStateLocal(state) {
  const clean = sanitizeForSave(state);
  localStorage.setItem(KEY, JSON.stringify(clean));
  return clean;
}

function readLegacyState() {
  for (const k of LEGACY_KEYS) {
    const raw = localStorage.getItem(k);
    if (!raw) continue;
    try {
      return JSON.parse(raw);
    } catch {
      // ignore
    }
  }
  return null;
}

function removeLegacyKeys() {
  for (const k of LEGACY_KEYS) {
    try {
      localStorage.removeItem(k);
    } catch {
      // ignore
    }
  }
}

export function sanitizeForSave(state) {
  // Phase 1: Keep state shape, but avoid persisting volatile timer runtime
  // (prevents very frequent DB writes and race conditions).
  const s = state && typeof state === "object" ? structuredClone(state) : state;
  if (!s || typeof s !== "object") return state;

  if (s.timer) {
    s.timer = { remainingSec: null, isRunning: false, isPaused: false, mode: "round" };
  }
  // stamp local persistence time (used for remote-vs-local hydration gate)
  try {
    s.__persist = { ...(s.__persist || {}), localSavedAt: Date.now() };
  } catch (_) {}

  return s;
}

export function migrate(s) {
  // wenn state noch "alt" ist: kein schemaVersion vorhanden
  const v = Number(s?.schemaVersion ?? 0);

  if (v === SCHEMA_VERSION) return s;

  let next = s;

  if (v < 1) next = migrate_v0_to_v1(next);

  // Final absichern
  next.schemaVersion = SCHEMA_VERSION;
  next = ensureDefaults(next);

  return next;
}

function migrate_v0_to_v1(old) {
  // v0 = dein aktuelles Shape (players/settings/schedule/results/ui/timer)
  // v1 = neues Shape: app/shared/modules

  const players = old?.players || { count: 12, names: {} };

  const settings = old?.settings || {};
  const tournament = {
    format: settings.format ?? "doubles",
    rounds: settings.rounds ?? 6,
    minutesPerRound: settings.minutesPerRound ?? 15,
    points: settings.points ?? { win: 2, draw: 1 },
  };

  const resultRules = settings.resultRules ?? {
    mode: "ONE_SET_OPEN",
    gamesToWinSet: 6,
    openSetWinByGames: 2,
    setWinByGames: 1,
    mtbPointsToWin: 10,
    mtbWinByPoints: 2,
  };

  const timerPrefs = settings.timerPrefs ?? {
    preCountdownSec: 5,
    speakLang: "de-DE",
    beepOnStart: true,
    beepOneMinute: true,
    countLast10: true,
  };

  return {
    schemaVersion: 1,

    app: {
      locale: navigator.language || "de-DE",
    },

    shared: {
      players,
      timerPrefs,
    },

    modules: {
      schleifchen: {
        tournament,
        resultRules,
        schedule: old?.schedule ?? [],
        results: old?.results ?? {},
        ui: old?.ui ?? { round: 1, phase: "idle", completedRound: 0 },
      },
      club: {
        // vorbereitet
      },
      minigames: {
        // vorbereitet
      },
    },

    // Timer-Runtime bleibt global (kann später auch modulbezogen werden)
    timer: old?.timer ?? {
      remainingSec: null,
      isRunning: false,
      isPaused: false,
      mode: "round",
    },
  };
}

function ensureDefaults(s) {
  // defensive defaults, damit keine undefineds existieren
  s.app ??= { locale: "de-DE" };
  s.shared ??= {};
  s.shared.players ??= { count: 12, names: {} };
  s.shared.timerPrefs ??= {
    preCountdownSec: 5,
    speakLang: "de-DE",
    beepOnStart: true,
    beepOneMinute: true,
    countLast10: true,
  };

  s.modules ??= {};
  s.modules.schleifchen ??= {};
  s.modules.schleifchen.tournament ??= {
    format: "doubles",
    rounds: 6,
    minutesPerRound: 15,
    points: { win: 2, draw: 1 },
  };
  s.modules.schleifchen.resultRules ??= {
    mode: "ONE_SET_OPEN",
    gamesToWinSet: 6,
    openSetWinByGames: 2,
    setWinByGames: 1,
    mtbPointsToWin: 10,
    mtbWinByPoints: 2,
  };
  s.modules.schleifchen.schedule ??= [];
  s.modules.schleifchen.results ??= {};
  s.modules.schleifchen.ui ??= { round: 1, phase: "idle", completedRound: 0 };

  s.timer ??= { remainingSec: null, isRunning: false, isPaused: false, mode: "round" };

  s.schemaVersion = SCHEMA_VERSION;
  return s;
}
